import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";
import "./AboutUs.css";

// Quill modules (used for the Intro section's "AARVJ Group" body)
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "align",
  "color",
  "background",
];

const PAGE_KEY = "about-us";
// Folder name used for uploads from this page. Must match the allow-list
// in uploadController.ts (advertise | about-us | mamangam-hall).
const UPLOAD_FOLDER = "about-us";

// ── Types ───────────────────────────────────────────────────────────────────
interface PageConfig {
  id: string;
  page_key: string;
  title: string | null;
  description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_active: boolean;
  sections: PageSection[];
}

interface PageSection {
  id: string;
  page_config_id: string;
  slug: string;
  title: string;
  display_order: number;
  is_active: boolean;
  data: any;
}

interface StatItem {
  number: string;
  label1: string;
  label2: string;
  label3: string;
  text: string;
  icon_type: "globe" | "location";
}

// HTML "What We Do" uses full background-image cards. The admin must
// upload a background image (not a small icon) and provide a short blurb.
interface ServiceItem {
  title: string;
  description: string;
  image_url: string;
}

// Slider images used in the intro section. Each entry is the uploaded URL
// (admin-uploaded files live under uploads/about-us/). `alt` is optional.
interface SliderImage {
  url: string;
  alt?: string;
}

interface MissionVisionCard {
  title: string;
  description: string;
  background_image: string;
}

// ── Default data factories (so forms always have something to render) ───────
const blankStats: StatItem[] = [
  {
    number: "",
    label1: "",
    label2: "",
    label3: "",
    text: "",
    icon_type: "location",
  },
  {
    number: "",
    label1: "",
    label2: "",
    label3: "",
    text: "",
    icon_type: "globe",
  },
  {
    number: "",
    label1: "",
    label2: "",
    label3: "",
    text: "",
    icon_type: "location",
  },
  {
    number: "",
    label1: "",
    label2: "",
    label3: "",
    text: "",
    icon_type: "location",
  },
  {
    number: "",
    label1: "",
    label2: "",
    label3: "",
    text: "",
    icon_type: "location",
  },
];

const blankService: ServiceItem = {
  title: "",
  description: "",
  image_url: "",
};
const blankMV: MissionVisionCard = {
  title: "",
  description: "",
  background_image: "",
};

// Helper: produce an initial servicesList of 8 blank rows (HTML has 8 cards)
const makeBlankServices = (): ServiceItem[] =>
  Array.from({ length: 8 }, () => ({ ...blankService }));

// ── Component ───────────────────────────────────────────────────────────────
const AboutUs: React.FC = () => {
  const [, setConfig] = useState<PageConfig | null>(null);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Hero / Page-level config state
  const [heroTitle, setHeroTitle] = useState("About Us");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Per-section state
  const [introHeading, setIntroHeading] = useState("");
  const [introContent, setIntroContent] = useState("");
  // The legacy single-image field. Kept for backward compatibility — it's
  // auto-populated from the first slider image on Save, so admins only ever
  // touch sliderImages directly.
  const [introImage, setIntroImage] = useState("");
  // New: array of slider images for the intro section. Falls back to the
  // legacy single image_url field on load for backward compatibility.
  const [sliderImages, setSliderImages] = useState<SliderImage[]>([]);
  const [savingIntro, setSavingIntro] = useState(false);
  const [uploadingSliderImage, setUploadingSliderImage] = useState(false);

  const [statsSinceText, setStatsSinceText] = useState("");
  const [statsTargetText, setStatsTargetText] = useState("");
  const [statsBackground, setStatsBackground] = useState("");
  const [statsLeftImage, setStatsLeftImage] = useState("");
  const [statsList, setStatsList] = useState<StatItem[]>(blankStats);
  const [savingStats, setSavingStats] = useState(false);
  const [uploadingStatsBg, setUploadingStatsBg] = useState(false);
  const [uploadingStatsLeft, setUploadingStatsLeft] = useState(false);

  // Our Cinemas — section-level copy (heading + subheading). The
  // individual cinemas (tab list + slider + level tag) are managed
  // separately on the Cinemas page.
  const [ourCinemasHeading, setOurCinemasHeading] = useState("Our Cinemas");
  const [ourCinemasSubheading, setOurCinemasSubheading] = useState(
    "Discover the MY Cinemas experience near you — premium screens, Dolby sound, and unforgettable films across Kerala.",
  );
  const [savingOurCinemas, setSavingOurCinemas] = useState(false);

  const [whatHeading, setWhatHeading] = useState("What We Do");
  const [servicesList, setServicesList] =
    useState<ServiceItem[]>(makeBlankServices());
  const [savingWhat, setSavingWhat] = useState(false);
  const [uploadingServiceImage, setUploadingServiceImage] = useState<
    number | null
  >(null);

  const [vision, setVision] = useState<MissionVisionCard>({ ...blankMV });
  const [mission, setMission] = useState<MissionVisionCard>({ ...blankMV });
  const [savingMV, setSavingMV] = useState(false);
  const [uploadingMVImage, setUploadingMVImage] = useState<
    "vision" | "mission" | null
  >(null);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getSectionByType = (type: string) =>
    sections.find((s) => s.data?.section_type === type);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPageConfigByKey(PAGE_KEY);
      if (response.success && response.data) {
        const data: PageConfig = response.data;
        setConfig(data);
        setSections(data.sections || []);

        // Local lookup against the freshly fetched sections.  Using the
        // `sections` state inside getSectionByType() would still see the
        // empty initial array because React state updates are async.
        const lookupSection = (type: string) =>
          (data.sections || []).find((s: any) => s.data?.section_type === type);

        // Hero / page-level
        setHeroTitle(data.title || "About Us");
        setMetaTitle(data.meta_title || "");
        setMetaDescription(data.meta_description || "");
        setIsActive(Boolean(data.is_active));

        // Intro
        const intro = lookupSection("intro");
        if (intro) {
          setIntroHeading(intro.data?.heading || "");
          setIntroContent(intro.data?.content || "");
          setIntroImage(intro.data?.image_url || "");
          // Prefer the new slider_images array; if absent, fall back to the
          // legacy single image_url so existing data is shown as a 1-slide
          // slider and admins can add more from the UI.
          if (
            Array.isArray(intro.data?.slider_images) &&
            intro.data.slider_images.length > 0
          ) {
            setSliderImages(intro.data.slider_images as SliderImage[]);
          } else if (intro.data?.image_url) {
            setSliderImages([{ url: intro.data.image_url }]);
          } else {
            setSliderImages([]);
          }
        }

        // Statistics
        const stats = lookupSection("statistics");
        if (stats) {
          setStatsSinceText(stats.data?.since_text || "");
          setStatsTargetText(stats.data?.target_text || "");
          setStatsBackground(stats.data?.background_image || "");
          setStatsLeftImage(stats.data?.left_image_url || "");
          if (Array.isArray(stats.data?.stats) && stats.data.stats.length > 0) {
            setStatsList(stats.data.stats as StatItem[]);
          }
        }

        // Our Cinemas — section-level copy only
        const ourCinemas = lookupSection("our_cinemas");
        if (ourCinemas) {
          setOurCinemasHeading(ourCinemas.data?.heading || "Our Cinemas");
          setOurCinemasSubheading(ourCinemas.data?.subheading || "");
        }

        // What We Do
        const what = lookupSection("what_we_do");
        if (what) {
          setWhatHeading(what.data?.heading || "What We Do");
          if (
            Array.isArray(what.data?.services) &&
            what.data.services.length > 0
          ) {
            // Normalise legacy icon_url entries into image_url on the fly
            const normalised: ServiceItem[] = what.data.services.map(
              (s: any) => ({
                title: s.title || "",
                description: s.description || "",
                image_url: s.image_url || s.icon_url || "",
              }),
            );
            setServicesList(normalised);
          }
        }

        // Mission & Vision
        const mv = lookupSection("mission_vision");
        if (mv) {
          setVision({
            title: mv.data?.vision?.title || "",
            description: mv.data?.vision?.description || "",
            background_image: mv.data?.vision?.background_image || "",
          });
          setMission({
            title: mv.data?.mission?.title || "",
            description: mv.data?.mission?.description || "",
            background_image: mv.data?.mission?.background_image || "",
          });
        }
      }
    } catch (error: any) {
      console.error("Error loading page config:", error);
      toast.error("Failed to load page configuration");
    } finally {
      setLoading(false);
    }
  };

  const refreshPage = async () => {
    const response = await adminService.getPageConfigByKey(PAGE_KEY);
    if (response.success && response.data) {
      setConfig(response.data);
      setSections(response.data.sections || []);
    }
  };

  // ── Page-level save (hero config) ──────────────────────────────────────────
  const handleSaveHeroConfig = async () => {
    try {
      setSavingConfig(true);
      const response = await adminService.updatePageConfig(PAGE_KEY, {
        title: heroTitle,
        metaTitle,
        metaDescription,
        isActive,
      });
      if (response.success) {
        toast.success("Page configuration saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save page configuration");
      }
    } catch (error: any) {
      console.error("Error saving hero config:", error);
      toast.error("Failed to save page configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Section save helper (create if missing, update if present) ─────────────
  const upsertSection = async (
    sectionType: string,
    title: string,
    slug: string,
    displayOrder: number,
    dataPayload: any,
  ) => {
    const existing = getSectionByType(sectionType);
    const sectionData = {
      title,
      slug,
      displayOrder,
      isActive: true,
      data: { section_type: sectionType, ...dataPayload },
    };
    if (existing) {
      return adminService.updatePageSection(PAGE_KEY, existing.id, sectionData);
    }
    return adminService.createPageSection(PAGE_KEY, sectionData);
  };

  // ── Intro section save ────────────────────────────────────────────────────
  const handleSaveIntro = async () => {
    try {
      setSavingIntro(true);
      // Keep the legacy image_url field in sync with the first slider image
      // so any old consumers still see something sensible.
      const legacyImageUrl =
        sliderImages.length > 0 ? sliderImages[0].url : introImage;
      const response = await upsertSection(
        "intro",
        "Intro - AARVJ Group",
        "intro",
        2,
        {
          heading: introHeading,
          content: introContent,
          image_url: legacyImageUrl,
          slider_images: sliderImages,
        },
      );
      if (response.success) {
        toast.success("Intro section saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save intro section");
      }
    } catch (error: any) {
      console.error("Error saving intro:", error);
      toast.error("Failed to save intro section");
    } finally {
      setSavingIntro(false);
    }
  };

  // ── Statistics section save ───────────────────────────────────────────────
  const handleSaveStats = async () => {
    try {
      setSavingStats(true);
      const cleanedStats = statsList.filter((s) => s.number.trim() !== "");
      const response = await upsertSection(
        "statistics",
        "Statistics / Landmark",
        "statistics",
        3,
        {
          since_text: statsSinceText,
          target_text: statsTargetText,
          background_image: statsBackground,
          left_image_url: statsLeftImage,
          stats: cleanedStats,
        },
      );
      if (response.success) {
        toast.success("Statistics section saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save statistics section");
      }
    } catch (error: any) {
      console.error("Error saving statistics:", error);
      toast.error("Failed to save statistics section");
    } finally {
      setSavingStats(false);
    }
  };

  // ── Our Cinemas section save ─────────────────────────────────────────
  // Section-level copy only (heading + subheading). The list of cinemas,
  // slider images, and per-cinema toggles are managed on the Cinemas page.
  const handleSaveOurCinemas = async () => {
    try {
      setSavingOurCinemas(true);
      const response = await upsertSection(
        "our_cinemas",
        "Our Cinemas",
        "our-cinemas",
        4,
        {
          heading: ourCinemasHeading,
          subheading: ourCinemasSubheading,
        },
      );
      if (response.success) {
        toast.success("Our Cinemas section saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Our Cinemas section");
      }
    } catch (error: any) {
      console.error("Error saving Our Cinemas:", error);
      toast.error("Failed to save Our Cinemas section");
    } finally {
      setSavingOurCinemas(false);
    }
  };

  // ── What We Do section save ───────────────────────────────────────────────
  const handleSaveWhatWeDo = async () => {
    try {
      setSavingWhat(true);
      const cleanedServices = servicesList.filter(
        (s) =>
          s.title.trim() !== "" ||
          s.description.trim() !== "" ||
          s.image_url.trim() !== "",
      );
      const response = await upsertSection(
        "what_we_do",
        "What We Do",
        "what-we-do",
        5,
        {
          heading: whatHeading,
          services: cleanedServices,
        },
      );
      if (response.success) {
        toast.success("What We Do section saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save What We Do section");
      }
    } catch (error: any) {
      console.error("Error saving What We Do:", error);
      toast.error("Failed to save What We Do section");
    } finally {
      setSavingWhat(false);
    }
  };

  // ── Mission & Vision section save ─────────────────────────────────────────
  const handleSaveMissionVision = async () => {
    try {
      setSavingMV(true);
      const response = await upsertSection(
        "mission_vision",
        "Mission & Vision",
        "mission-vision",
        6,
        {
          vision,
          mission,
        },
      );
      if (response.success) {
        toast.success("Mission & Vision section saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Mission & Vision");
      }
    } catch (error: any) {
      console.error("Error saving Mission & Vision:", error);
      toast.error("Failed to save Mission & Vision");
    } finally {
      setSavingMV(false);
    }
  };

  // ── Image upload handlers ──────────────────────────────────────────────────
  // Upload + auto-save helper.  After the file is uploaded to disk the
  // corresponding section is persisted to the DB immediately so the URL
  // survives a page refresh (no separate Save click required).
  const uploadAndPersistSection = async (
    file: File,
    setUploading: (b: boolean) => void,
    sectionType: string,
    sectionTitle: string,
    sectionSlug: string,
    displayOrder: number,
    dataPayload: any,
  ) => {
    try {
      setUploading(true);
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        const existing = getSectionByType(sectionType);
        const finalPayload = {
          title: sectionTitle,
          slug: sectionSlug,
          displayOrder,
          isActive: true,
          data: { section_type: sectionType, ...dataPayload, image_url: url },
        };
        const saveResp = existing
          ? await adminService.updatePageSection(
              PAGE_KEY,
              existing.id,
              finalPayload,
            )
          : await adminService.createPageSection(PAGE_KEY, finalPayload);
        if (saveResp.success) {
          toast.success("Image uploaded & saved");
          refreshPage();
          return url;
        }
        toast.error(saveResp.message || "Save failed after upload");
      } else {
        toast.error("Upload failed");
      }
    } catch (error: any) {
      console.error("Error uploading & saving:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
    return null;
  };

  const uploadImage = async (
    file: File,
    onSuccess: (url: string) => void,
    setUploading: (b: boolean) => void,
  ): Promise<string | null> => {
    try {
      setUploading(true);
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        onSuccess(url);
        toast.success("Image uploaded");
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadSliderImage = async (file: File): Promise<string | null> => {
    setUploadingSliderImage(true);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        const url = response.data.url || response.data.filename;
        setSliderImages((prev) => [...prev, { url }]);
        toast.success("Image added to slider");
        return url;
      }
      toast.error("Upload failed");
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingSliderImage(false);
    }
  };

  const uploadStatsBackground = async (file: File): Promise<string | null> => {
    const url = await uploadAndPersistSection(
      file,
      setUploadingStatsBg,
      "statistics",
      "Statistics / Landmark",
      "statistics",
      3,
      {
        since_text: statsSinceText,
        target_text: statsTargetText,
        stats: statsList,
      },
    );
    if (url) setStatsBackground(url);
    return url;
  };

  const uploadStatsLeftImage = async (file: File): Promise<string | null> => {
    setUploadingStatsLeft(true);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (!response.success || !response.data) {
        toast.error("Upload failed");
        return null;
      }
      const url = response.data.url || response.data.filename;
      setStatsLeftImage(url);
      const existing = getSectionByType("statistics");
      const cleanedStats = statsList.filter((s) => s.number.trim() !== "");
      const finalPayload = {
        title: "Statistics / Landmark",
        slug: "statistics",
        displayOrder: 3,
        isActive: true,
        data: {
          section_type: "statistics",
          since_text: statsSinceText,
          target_text: statsTargetText,
          background_image: statsBackground,
          left_image_url: url,
          stats: cleanedStats,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(
            PAGE_KEY,
            existing.id,
            finalPayload,
          )
        : await adminService.createPageSection(PAGE_KEY, finalPayload);
      if (saveResp.success) {
        toast.success("Left image uploaded & saved");
        refreshPage();
        return url;
      }
      toast.error(saveResp.message || "Save failed after upload");
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload left image");
      return null;
    } finally {
      setUploadingStatsLeft(false);
    }
  };

  const uploadServiceImage = async (
    file: File,
    index: number,
  ): Promise<string | null> => {
    setUploadingServiceImage(index);
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (!response.success || !response.data) {
        toast.error("Upload failed");
        return null;
      }
      const url = response.data.url || response.data.filename;
      const updatedServices = servicesList.map((s, i) =>
        i === index ? { ...s, image_url: url } : s,
      );
      setServicesList(updatedServices);
      const existing = getSectionByType("what_we_do");
      const cleanedServices = updatedServices.filter(
        (s) =>
          s.title.trim() !== "" ||
          s.description.trim() !== "" ||
          s.image_url.trim() !== "",
      );
      const finalPayload = {
        title: "What We Do",
        slug: "what-we-do",
        displayOrder: 5,
        isActive: true,
        data: {
          section_type: "what_we_do",
          heading: whatHeading,
          services: cleanedServices,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(
            PAGE_KEY,
            existing.id,
            finalPayload,
          )
        : await adminService.createPageSection(PAGE_KEY, finalPayload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      toast.error(saveResp.message || "Save failed after upload");
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingServiceImage(null);
    }
  };

  const uploadVisionImage = async (file: File): Promise<string | null> => {
    setUploadingMVImage("vision");
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (!response.success || !response.data) {
        toast.error("Upload failed");
        return null;
      }
      const url = response.data.url || response.data.filename;
      const updatedVision = { ...vision, background_image: url };
      setVision(updatedVision);
      const existing = getSectionByType("mission_vision");
      const finalPayload = {
        title: "Mission & Vision",
        slug: "mission-vision",
        displayOrder: 6,
        isActive: true,
        data: {
          section_type: "mission_vision",
          vision: updatedVision,
          mission,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(
            PAGE_KEY,
            existing.id,
            finalPayload,
          )
        : await adminService.createPageSection(PAGE_KEY, finalPayload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      toast.error(saveResp.message || "Save failed after upload");
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingMVImage(null);
    }
  };

  const uploadMissionImage = async (file: File): Promise<string | null> => {
    setUploadingMVImage("mission");
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (!response.success || !response.data) {
        toast.error("Upload failed");
        return null;
      }
      const url = response.data.url || response.data.filename;
      const updatedMission = { ...mission, background_image: url };
      setMission(updatedMission);
      const existing = getSectionByType("mission_vision");
      const finalPayload = {
        title: "Mission & Vision",
        slug: "mission-vision",
        displayOrder: 6,
        isActive: true,
        data: {
          section_type: "mission_vision",
          vision,
          mission: updatedMission,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(
            PAGE_KEY,
            existing.id,
            finalPayload,
          )
        : await adminService.createPageSection(PAGE_KEY, finalPayload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      toast.error(saveResp.message || "Save failed after upload");
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingMVImage(null);
    }
  };

  const removeSliderImage = (idx: number) => {
    setSliderImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (idx === 0) {
        setIntroImage(next.length > 0 ? next[0].url : "");
      }
      return next;
    });
  };

  // ── Stats table helpers ────────────────────────────────────────────────────
  const addStatRow = () =>
    setStatsList([
      ...statsList,
      {
        number: "",
        label1: "",
        label2: "",
        label3: "",
        text: "",
        icon_type: "location",
      },
    ]);

  const removeStatRow = (i: number) =>
    setStatsList(statsList.filter((_, idx) => idx !== i));

  const updateStatRow = (i: number, field: keyof StatItem, value: string) => {
    setStatsList((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  // ── Services helpers ──────────────────────────────────────────────────────
  const addService = () =>
    setServicesList([...servicesList, { ...blankService }]);

  const removeService = (i: number) =>
    setServicesList(servicesList.filter((_, idx) => idx !== i));

  const updateService = (
    i: number,
    field: keyof ServiceItem,
    value: string,
  ) => {
    setServicesList((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>About Us - Admin</title>
      </Helmet>

      <div className="container-fluid p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">About Us</h2>
            <p className="text-muted mb-0">
              Manage content, images, and sections for the public About Us page
            </p>
          </div>
        </div>

        {/* ─── 1) Page-level / Hero config ──────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-house-door me-2"></i>
              Page Configuration
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Page Title (shown in the hero banner)</Form.Label>
                  <Form.Control
                    type="text"
                    value={heroTitle}
                    onChange={(e) => setHeroTitle(e.target.value)}
                    placeholder="About Us"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Title (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder="About MY Cinemas"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Meta Description (SEO)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Learn about MY Cinemas…"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="about-page-active"
                label="Page is active (visible on website)"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveHeroConfig}
              disabled={savingConfig}
            >
              {savingConfig ? "Saving…" : "Save Page Configuration"}
            </Button>
          </Card.Body>
        </Card>

        {/* ─── 2) Intro section ─────────────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-card-text me-2"></i>
              Intro Section — "AARVJ Group"
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Heading</Form.Label>
              <Form.Control
                type="text"
                value={introHeading}
                onChange={(e) => setIntroHeading(e.target.value)}
                placeholder="Welcome to AARVJ Group"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content (rich text)</Form.Label>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                value={introContent}
                onChange={setIntroContent}
                placeholder="Tell the brand story…"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Intro Slider Images
                <span className="text-muted ms-2" style={{ fontWeight: 400 }}>
                  ({sliderImages.length} image
                  {sliderImages.length === 1 ? "" : "s"})
                </span>
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {sliderImages.map((img, i) => (
                  <div
                    key={`${img.url}-${i}`}
                    style={{
                      position: "relative",
                      width: 120,
                      height: 90,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #dee2e6",
                      background: "#f8f9fa",
                    }}
                  >
                    <img
                      src={img.url}
                      alt={`Slide ${i + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => removeSliderImage(i)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        padding: "0 6px",
                        lineHeight: 1,
                      }}
                      title="Remove image"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                    {i === 0 && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: 4,
                          left: 4,
                          background: "rgba(46,49,146,0.85)",
                          color: "#fff",
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: 0.5,
                        }}
                      >
                        FIRST
                      </span>
                    )}
                  </div>
                ))}
                <div className="position-relative">
                  <ImageUploadField
                    spec={IMAGE_SPECS.aboutIntroSlider}
                    onUpload={uploadSliderImage}
                    onChange={setIntroImage}
                    disabled={uploadingSliderImage}
                    showPreview={false}
                    buttonLabel={uploadingSliderImage ? "Uploading…" : "Add Image"}
                    buttonVariant="outline-secondary"
                    buttonIcon="bi-plus-circle"
                    fieldId="about-intro-slider-upload"
                  />
                </div>
              </div>
              <Form.Text className="text-muted">
                Add one or more images. The first image is shown by default
                and the slider auto-rotates on the website.
              </Form.Text>
            </Form.Group>

            {/* Legacy image_url field — auto-managed from the first slider
                image on Save. Hidden behind an info badge so admins can see
                what value will be written but can't get it out of sync with
                the slider. */}
            {introImage && (
              <div className="mb-3">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Legacy <code>image_url</code> field will be synced with the
                  first slider image:{" "}
                  <span
                    style={{
                      wordBreak: "break-all",
                      color: "#495057",
                    }}
                  >
                    {introImage}
                  </span>
                </small>
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleSaveIntro}
              disabled={savingIntro}
            >
              {savingIntro ? "Saving…" : "Save Intro Section"}
            </Button>
          </Card.Body>
        </Card>

        {/* ─── 3) Statistics section ────────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-bar-chart me-2"></i>
              Statistics / Landmark Section
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Since text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={statsSinceText}
                    onChange={(e) => setStatsSinceText(e.target.value)}
                    placeholder="Since 2018, we have…"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target / quote text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={statsTargetText}
                    onChange={(e) => setStatsTargetText(e.target.value)}
                    placeholder="Our target is to…"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Background image</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={statsBackground}
                  onChange={(e) => setStatsBackground(e.target.value)}
                  placeholder="Upload or paste image URL"
                />
                <ImageUploadField
                  spec={IMAGE_SPECS.aboutStatsBackground}
                  onUpload={uploadStatsBackground}
                  onChange={(url) => setStatsBackground(url)}
                  disabled={uploadingStatsBg}
                  buttonVariant="outline-secondary"
                  showPreview={false}
                />
              </InputGroup>
              {statsBackground && (
                <div className="mt-2">
                  <img
                    src={statsBackground}
                    alt="Background preview"
                    style={{ maxWidth: 320, borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Left image (shown next to the stats)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={statsLeftImage}
                  onChange={(e) => setStatsLeftImage(e.target.value)}
                  placeholder="Upload or paste image URL"
                />
                <ImageUploadField
                  spec={IMAGE_SPECS.aboutStatsLeftImage}
                  onUpload={uploadStatsLeftImage}
                  onChange={(url) => setStatsLeftImage(url)}
                  disabled={uploadingStatsLeft}
                  buttonVariant="outline-secondary"
                  showPreview={false}
                />
              </InputGroup>
              {statsLeftImage && (
                <div className="mt-2">
                  <img
                    src={statsLeftImage}
                    alt="Left image preview"
                    style={{ maxWidth: 240, borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Stats rows</strong>
              <Button size="sm" variant="success" onClick={addStatRow}>
                <i className="bi bi-plus-circle me-1"></i>
                Add row
              </Button>
            </div>
            <Table responsive bordered size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Number</th>
                  <th>Label 1</th>
                  <th>Label 2</th>
                  <th>Label 3</th>
                  <th>Text</th>
                  <th>Icon</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {statsList.map((s, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={s.number}
                        onChange={(e) =>
                          updateStatRow(i, "number", e.target.value)
                        }
                        placeholder="50+"
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={s.label1}
                        onChange={(e) =>
                          updateStatRow(i, "label1", e.target.value)
                        }
                        placeholder="Cinema"
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={s.label2}
                        onChange={(e) =>
                          updateStatRow(i, "label2", e.target.value)
                        }
                        placeholder="s"
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={s.label3}
                        onChange={(e) =>
                          updateStatRow(i, "label3", e.target.value)
                        }
                        placeholder=""
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={s.text}
                        onChange={(e) =>
                          updateStatRow(i, "text", e.target.value)
                        }
                        placeholder="Across India"
                      />
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={s.icon_type}
                        onChange={(e) =>
                          updateStatRow(
                            i,
                            "icon_type",
                            e.target.value as StatItem["icon_type"],
                          )
                        }
                      >
                        <option value="location">Location pin</option>
                        <option value="globe">Globe</option>
                      </Form.Select>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeStatRow(i)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Button
              variant="primary"
              onClick={handleSaveStats}
              disabled={savingStats}
            >
              {savingStats ? "Saving…" : "Save Statistics Section"}
            </Button>
          </Card.Body>
        </Card>

        {/* ─── 4) Our Cinemas — section header (display_order 4) ──────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-collection-play me-2"></i>
              Our Cinemas Section
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Section heading</Form.Label>
              <Form.Control
                type="text"
                value={ourCinemasHeading}
                onChange={(e) => setOurCinemasHeading(e.target.value)}
                placeholder="Our Cinemas"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Section subheading</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ourCinemasSubheading}
                onChange={(e) => setOurCinemasSubheading(e.target.value)}
                placeholder="Discover the MY Cinemas experience near you — premium screens, Dolby sound, and unforgettable films across Kerala."
              />
            </Form.Group>
            <Alert variant="info" className="mb-3 py-2">
              <small>
                <i className="bi bi-info-circle me-1"></i>
                The list of cinemas, slider images, level tags, and the
                "Show in About Us page" toggle are managed on the{" "}
                <strong>Cinemas</strong> page. Edit a cinema there to
                control what appears in this section's tab list.
              </small>
            </Alert>
            <Button
              variant="primary"
              onClick={handleSaveOurCinemas}
              disabled={savingOurCinemas}
            >
              {savingOurCinemas ? "Saving…" : "Save Our Cinemas Section"}
            </Button>
          </Card.Body>
        </Card>

        {/* ─── 5) What We Do section ────────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-grid me-2"></i>
              What We Do Section
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Section heading</Form.Label>
              <Form.Control
                type="text"
                value={whatHeading}
                onChange={(e) => setWhatHeading(e.target.value)}
                placeholder="What We Do"
              />
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Service tiles (8 cards matching the HTML design)</strong>
              <Button size="sm" variant="success" onClick={addService}>
                <i className="bi bi-plus-circle me-1"></i>
                Add tile
              </Button>
            </div>
            <Row>
              {servicesList.map((service, i) => (
                <Col key={i} md={6} lg={3} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Form.Group className="mb-2">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          size="sm"
                          value={service.title}
                          onChange={(e) =>
                            updateService(i, "title", e.target.value)
                          }
                          placeholder="Cinema Operations"
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Description (short blurb)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          size="sm"
                          value={service.description}
                          onChange={(e) =>
                            updateService(i, "description", e.target.value)
                          }
                          placeholder="End-to-end operational excellence…"
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Background image</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            value={service.image_url}
                            onChange={(e) =>
                              updateService(i, "image_url", e.target.value)
                            }
                            placeholder="Upload or paste image URL"
                          />
                          <ImageUploadField
                            spec={IMAGE_SPECS.aboutWhatWeDoTile}
                            onUpload={(file) => uploadServiceImage(file, i)}
                            onChange={(url) =>
                              updateService(i, "image_url", url)
                            }
                            disabled={uploadingServiceImage === i}
                            buttonVariant="outline-secondary"
                            buttonIcon="bi-upload"
                            buttonLabel=""
                            showPreview={false}
                          />
                        </InputGroup>
                        {service.image_url && (
                          <div className="mt-2">
                            <img
                              src={service.image_url}
                              alt={`Service ${i + 1} preview`}
                              style={{
                                width: "100%",
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          </div>
                        )}
                      </Form.Group>
                      <div className="d-flex justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => removeService(i)}
                        >
                          <i className="bi bi-trash"></i> Remove
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <Button
              variant="primary"
              onClick={handleSaveWhatWeDo}
              disabled={savingWhat}
            >
              {savingWhat ? "Saving…" : "Save What We Do Section"}
            </Button>
          </Card.Body>
        </Card>

        {/* ─── 6) Mission & Vision section ───────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-bullseye me-2"></i>
              Mission & Vision Section
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h5>Vision</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={vision.title}
                    onChange={(e) =>
                      setVision((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Our Vision"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={vision.description}
                    onChange={(e) =>
                      setVision((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="To be…"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Background image</Form.Label>
                  <InputGroup>
                    <Form.Control
                      value={vision.background_image}
                      onChange={(e) =>
                        setVision((prev) => ({
                          ...prev,
                          background_image: e.target.value,
                        }))
                      }
                      placeholder="Upload or paste URL"
                    />
                    <ImageUploadField
                      spec={IMAGE_SPECS.aboutVision}
                      onUpload={uploadVisionImage}
                      onChange={(url) =>
                        setVision((prev) => ({
                          ...prev,
                          background_image: url,
                        }))
                      }
                      disabled={uploadingMVImage === "vision"}
                      buttonVariant="outline-secondary"
                      showPreview={false}
                    />
                  </InputGroup>
                  {vision.background_image && (
                    <div className="mt-2">
                      <img
                        src={vision.background_image}
                        alt="Vision bg preview"
                        style={{ maxWidth: 240, borderRadius: 8 }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <h5>Mission</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={mission.title}
                    onChange={(e) =>
                      setMission((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Our Mission"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={mission.description}
                    onChange={(e) =>
                      setMission((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="To deliver…"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Background image</Form.Label>
                  <InputGroup>
                    <Form.Control
                      value={mission.background_image}
                      onChange={(e) =>
                        setMission((prev) => ({
                          ...prev,
                          background_image: e.target.value,
                        }))
                      }
                      placeholder="Upload or paste URL"
                    />
                    <ImageUploadField
                      spec={IMAGE_SPECS.aboutMission}
                      onUpload={uploadMissionImage}
                      onChange={(url) =>
                        setMission((prev) => ({
                          ...prev,
                          background_image: url,
                        }))
                      }
                      disabled={uploadingMVImage === "mission"}
                      buttonVariant="outline-secondary"
                      showPreview={false}
                    />
                  </InputGroup>
                  {mission.background_image && (
                    <div className="mt-2">
                      <img
                        src={mission.background_image}
                        alt="Mission bg preview"
                        style={{ maxWidth: 240, borderRadius: 8 }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Button
              variant="primary"
              onClick={handleSaveMissionVision}
              disabled={savingMV}
            >
              {savingMV ? "Saving…" : "Save Mission & Vision Section"}
            </Button>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default AboutUs;
